import React from 'react';
import { ListingsCollection as ListingsCollectionQuery } from '../gql/graphql';
import { RCCard, RCPriceBlock } from '@reverbdotcom/cadence/components';
import { PlaceholderListingRowCard } from './listing_row_card';
import { listingItemPagePath } from '../url_helpers';
import classNames from 'classnames';
import { WatchBadge, DisplayStyle } from './watch_badge';
import {
  buildListingForBuyer,
  getA11yPriceDescription,
} from '../listing_card_helpers';
import { trackEvent, buildListingClickEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import ImageCarousel from '@reverbdotcom/commons/src/components/image_carousel';

const MAX_COUNT = 8;
const MIN_COUNT = 5;

interface Props {
  listings?: ListingsCollectionQuery.Fragment[];
  loading?: boolean;
  handleCreateWatchResult?: () => void;
  parentComponentName?: string;
  displayMobileMosaic?: boolean;
}

export default function MosaicListingsCollection(props: Props) {
  const listings = props.listings.slice(0, MAX_COUNT);

  if (listings.length < MIN_COUNT) { return null; }

  const gridClasses = classNames(
    'mosaic-listings-collection__grid',
    {
      'mosaic-listings-collection__adjust-grid': props.listings.length <= 6 && !props.displayMobileMosaic,
      'mosaic-listings-collection__adjust-grid-mobile-mosaic': props.listings.length <= 6 && props.displayMobileMosaic,
    },
  );
  function renderGrid() {
    return (
      <div className={gridClasses}>
        {listings.map(function (listing, index) {
          const itemClasses = classNames(
            {
              'mosaic-listings-collection__item': !props.displayMobileMosaic,
              'grid-col-span-2 grid-row-span-2 mobile-grid-col-span-1 mobile-grid-row-span-1': index == 0 && !props.displayMobileMosaic,
              'grid-col-span-1 grid-row-span-1': index >= 1 && !props.displayMobileMosaic,
              'd-none mobile-d-block': index > 4 && !props.displayMobileMosaic,
            },
            {
              'mosaic-listings-collection__item-mobile-mosaic': props.displayMobileMosaic,
              'grid-col-span-2 grid-row-span-2 mobile-grid-col-span-12 mobile-grid-row-span-2': index == 0 && props.displayMobileMosaic,
              'grid-col-span-1 grid-row-span-1 mobile-grid-col-span-6': index >= 1 && props.displayMobileMosaic,
              'd-none': index > 4 && props.displayMobileMosaic,
            },
          );
          return (
            <div
              className={itemClasses}
              key={listing.id}
            >
              {renderCard(listing, index)}
            </div>
          );
        })}
      </div>
    );
  }

  function trackOnClick(listing, index) {
    trackEvent(
      buildListingClickEvent({
        listing: listing,
        componentName: props.parentComponentName,
        displayStyle: 'mosaic',
        position: index,
        countryCode: listing.shop.address?.country?.countryCode,
      }),
    );
  }

  function renderCard(listing, index) {
    if (props.loading) {
      return (<PlaceholderListingRowCard />);
    }

    const decoratedListing = buildListingForBuyer(listing);
    const listingImages = listing.images.map(i => (i.source));

    return (
      <RCCard>
        <a
          href={listingItemPagePath(listing)}
          target="_blank"
          rel="noreferrer"
          className="d-block pos-relative"
          onClick={() => trackOnClick(listing, index)}
        >
          <div className="pos-absolute pos-top-2 pos-right-2">
            <WatchBadge
              listing={listing}
              displayStyle={DisplayStyle.RC_FAVORITE_BUTTON}
              parentComponentName={props.parentComponentName}
              handleCreateWatchResult={props.handleCreateWatchResult}
              useToast
            />
          </div>
          <div className={classNames(
            'bd-1',
            'bd--offwhite',
            'overflow-hidden',
            'bd-radius-xl',
          )}>
            <ImageCarousel
              images={listingImages}
              imgAlt={listing.title}
            />
          </div>
          <div className={classNames(
            'pos-absolute',
            'pos-bottom-2',
            'pos-left-2',
            'bg-white',
            'opacity-90',
            'padding-1',
            'bd-radius-lg',
          )}>
            <RCPriceBlock
              priceDisplay={decoratedListing.primaryPriceDisplay}
              discountPriceDisplay={decoratedListing.discountedPriceDisplay}
              a11yPriceDescription={getA11yPriceDescription(decoratedListing)}
              highlightDiscountedPrice
              smallText
            />
          </div>
        </a>
      </RCCard>
    );
  }

  return (
    renderGrid()
  );
}
